import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CashFlowStats from "./CashFlowStats";
import MyProjects from "./MyProjects";
import Button from "../../components/Button";
import { BASE_URL } from "utils/constant";
import Logo from "../../assets/site.png";
import SubscriptionModal from "./Subscription/SubscriptionModal";
import ModernB2BLoading from "components/UI_components/ModernLoading";
import {
  UserAPI,
  SubscriptionAPI,
  OrgAPI,
  SitesAPI,
} from "api/dashboard/service";
// API functions
const apiClient = {
  fetchUserData: UserAPI.getUserDetails,

  fetchOrganizations: OrgAPI.listForUser,

  fetchProjectSites: async (organizationId, accessToken) => {
    const data = await SitesAPI.listByOrganization(organizationId);

    let sitesArr = [];
    if (Array.isArray(data)) {
      sitesArr = data;
    } else if (Array.isArray(data.data)) {
      sitesArr = data.data;
    }
    return sitesArr;
  },

  fetchSiteData: async (siteId, accessToken) => {
    const data = await SitesAPI.getById(siteId);
    if (!data) throw new Error("Failed to fetch site data");
    return data;
  },

  fetchOrgTransactions: async (organizationId, accessToken) => {
    const data = await OrgAPI.getTransactions(organizationId);
    return data;
  },

  checkSubscription: async (organizationId, accessToken) => {
    const data = await SubscriptionAPI.checkUserSubscription(organizationId);
    if (!data) throw new Error("Failed to check subscription");
    return data;
  },
};

// Custom hooks for React Query
const useUserData = (accessToken) => {
  return useQuery({
    queryKey: ["userData", accessToken],
    queryFn: () => apiClient.fetchUserData(accessToken),
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

const useOrganizations = (accessToken, isOwner) => {
  return useQuery({
    queryKey: ["organizations", accessToken],
    queryFn: () => apiClient.fetchOrganizations(accessToken),
    enabled: !!accessToken && !isOwner,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

const useProjectSites = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["projectSites", organizationId],
    queryFn: () => apiClient.fetchProjectSites(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000,
  });
};

const useSiteData = (siteId, accessToken) => {
  return useQuery({
    queryKey: ["siteData", siteId],
    queryFn: () => apiClient.fetchSiteData(siteId, accessToken),
    enabled: !!siteId && !!accessToken,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};

const useOrgTransactions = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["orgTransactions", organizationId],
    queryFn: () => apiClient.fetchOrgTransactions(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 1 * 60 * 1000, // 1 minute for financial data
    cacheTime: 5 * 60 * 1000,
  });
};

const useSubscription = (organizationId, accessToken) => {
  return useQuery({
    queryKey: ["subscription", organizationId],
    queryFn: () => apiClient.checkSubscription(organizationId, accessToken),
    enabled: !!organizationId && !!accessToken,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Utility functions
const calculateOrgSummary = (payment_summary) => {
  const total_paid = Number(payment_summary?.total_paid || 0);
  const total_pending = Number(payment_summary?.total_pending || 0);
  const total_received = Number(payment_summary?.total_received || 0);
  const total_remaining = total_received - total_pending;

  return {
    total_paid,
    total_pending,
    total_received,
    total_remaining,
  };
};

const calculateProjectAnalytics = (
  selectedProjectId,
  orgTransactions,
  pendingTransactions,
  selectedSiteData
) => {
  if (!selectedProjectId) {
    return {
      estimated_budget: 0,
      dues: 0,
      received: 0,
      expenses: 0,
    };
  }

  const projectTxns = orgTransactions.filter(
    (txn) => String(txn.site_id) === String(selectedProjectId)
  );

  let estimated_budget = 0;
  if (selectedSiteData?.data?.esrimated_cost) {
    estimated_budget = selectedSiteData.data.esrimated_cost;
  } else if (projectTxns.length > 0) {
    estimated_budget = projectTxns[0].estimated_budget || 0;
  }

  const dues = pendingTransactions
    .filter((txn) => String(txn.site_id) === String(selectedProjectId))
    .reduce((acc, txn) => acc + Number(txn.remaining_payment || 0), 0);

  const received = projectTxns
    .filter((txn) => txn.category === "Received")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  const refund = projectTxns
    .filter((txn) => txn.category === "Refund")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  const expenses = projectTxns
    .filter((txn) => txn.category !== "Received" && txn.category !== "Refund")
    .reduce((acc, txn) => acc + Number(txn.amount || txn.paid_amount || 0), 0);

  return { estimated_budget, dues, received, expenses };
};

const MainContent = ({
  filteredSites,
  onOrganizationClick,
  organizationId,
  onNewProjectClick,
}) => {
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const queryClient = useQueryClient();
  const accessToken = localStorage.getItem("access");

  // React Query hooks
  const userDataQuery = useUserData(accessToken);
  const isOwner =
    userDataQuery.data?.status &&
    userDataQuery.data.data.profession === "owner";

  const organizationsQuery = useOrganizations(accessToken, isOwner);
  const projectSitesQuery = useProjectSites(organizationId, accessToken);
  const siteDataQuery = useSiteData(selectedProjectId, accessToken);
  const orgTransactionsQuery = useOrgTransactions(organizationId, accessToken);
  const subscriptionQuery = useSubscription(organizationId, accessToken);

  // Set initial selected project when sites are loaded
  useEffect(() => {
    if (projectSitesQuery.data?.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projectSitesQuery.data[0].id);
    }
  }, [projectSitesQuery.data, selectedProjectId]);

  // Reset selected project when organization changes
  useEffect(() => {
    if (!organizationId) {
      setSelectedProjectId("");
    }
  }, [organizationId]);

  // Derived state
  const organizations = organizationsQuery.data?.data || [];
  const projectSites = projectSitesQuery.data || [];
  const orgTransactions = orgTransactionsQuery.data?.data || [];
  const pendingTransactions =
    orgTransactionsQuery.data?.pending_transactions || [];
  const hasActiveSubscription =
    subscriptionQuery.data?.status &&
    subscriptionQuery.data.data?.active_flag &&
    subscriptionQuery.data.data?.valid_till > new Date().toISOString();

  const orgSummary = calculateOrgSummary(
    orgTransactionsQuery.data?.payment_summary
  );
  const projectAnalytics = calculateProjectAnalytics(
    selectedProjectId,
    orgTransactions,
    pendingTransactions,
    siteDataQuery.data
  );

  // Handle subscription modal close
  const handleSubscriptionClose = () => {
    setSubscriptionOpen(false);
    queryClient.invalidateQueries({
      queryKey: ["subscription", organizationId],
    });
  };

  // Loading states
  const isInitialLoading =
    userDataQuery.isLoading ||
    (organizationsQuery.isLoading && !isOwner) ||
    (subscriptionQuery.isLoading && organizationId);

  if (isInitialLoading) {
    return <ModernB2BLoading />;
  }

  if (subscriptionOpen && isOwner) {
    return (
      <SubscriptionModal
        isOpen={subscriptionOpen}
        onClose={handleSubscriptionClose}
      />
    );
  }

  return (
    <div style={styles.container} className="scroll-container">
      {isOwner && organizationId && hasActiveSubscription && (
        <div className="flex flex-col gap-6 mb-6">
          {/* Finance Overview Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Company Finances Card */}
            <div className="bg-white rounded-xl p-4 shadow-md border border-slate-200/60">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-slate-700 text-sm font-semibold tracking-wide uppercase ml-2">
                  Company Finances
                </h3>
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  {/* <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg> */}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Total Expenses */}
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-4 border border-rose-100 hover:shadow-md hover:border-rose-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-rose-300 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Expenses</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-rose-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${orgSummary.total_paid.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Total Dues */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100 hover:shadow-md hover:border-amber-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-amber-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Dues</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-amber-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${orgSummary.total_pending.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Total Received */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 hover:shadow-md hover:border-emerald-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-emerald-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Received</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-emerald-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${orgSummary.total_received.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Balance */}
                <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl p-4 border border-cyan-100 hover:shadow-md hover:border-cyan-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 rounded-md ${orgSummary.total_remaining < 0 ? 'bg-red-500' : 'bg-cyan-500'} flex items-center justify-center`}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Balance</span>
                  </div>
                  <p className={`text-slate-800 font-bold text-lg transition-colors ${orgSummary.total_remaining < 0 ? 'group-hover:text-red-700' : 'group-hover:text-cyan-700'}`}>
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${orgSummary.total_remaining.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Project Analytics Card */}
            <div className="bg-white rounded-xl p-4 shadow-md border border-slate-200/60">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-slate-700 text-sm font-semibold tracking-wide uppercase">
                  Project Analytics
                </h3>
                <select
                  className="bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 border-0 cursor-pointer focus:ring-2 focus:ring-violet-500 focus:ring-offset-1 transition-all duration-200 appearance-none pr-8"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '16px'
                  }}
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={projectSitesQuery.isLoading || projectSitesQuery.isError}
                >
                  {projectSitesQuery.isLoading ? (
                    <option>Loading...</option>
                  ) : projectSitesQuery.isError ? (
                    <option>Error</option>
                  ) : projectSites.length === 0 ? (
                    <option>No projects</option>
                  ) : (
                    projectSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.site_name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Estimated Budget */}
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-100 hover:shadow-md hover:border-violet-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-violet-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Budget</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-violet-700 transition-colors">
                    {siteDataQuery.isLoading || orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${projectAnalytics.estimated_budget.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Project Dues */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100 hover:shadow-md hover:border-amber-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-amber-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Dues</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-amber-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${projectAnalytics.dues.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Total Received */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 hover:shadow-md hover:border-emerald-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-emerald-500 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Received</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-emerald-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${projectAnalytics.received.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>

                {/* Total Expenses */}
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-4 border border-rose-100 hover:shadow-md hover:border-rose-200 transition-all duration-200 group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-rose-300 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </div>
                    <span className="text-slate-500 text-xs font-medium">Expenses</span>
                  </div>
                  <p className="text-slate-800 font-bold text-lg group-hover:text-rose-700 transition-colors">
                    {orgTransactionsQuery.isLoading
                      ? "..."
                      : `₹${projectAnalytics.expenses.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isOwner && !userDataQuery.isLoading && organizationsQuery.isFetched ? (
        <div className="flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">Your Organizi</h1>
          {organizations && organizations.length > 0 ? (
            <div className="flex flex-row items-center justify-start gap-4">
              {organizations.map((org, index) => (
                <div
                  key={index}
                  style={styles.orgItem}
                  className="truncate"
                  onClick={() => onOrganizationClick(org)}
                >
                  <img
                    src={org?.logo_image_file || Logo}
                    alt="Organization Logo"
                    style={styles.orgLogo}
                    className={`${org.id === organizationId
                      ? "border-3 border-[#3b0069]"
                      : "border-2 border-[#9A9A9A]"
                      }`}
                  />
                  <span style={styles.orgName} className="truncate">
                    {org.organization_name}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg text-center w-full">
              <div className="text-gray-400 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No Organizations Found
              </h3>
              <p className="text-gray-600 mb-2">
                You are not associated with any organizations currently.
              </p>
              <div className="flex items-center justify-center text-gray-600 mt-2">
                <p>
                  Please ask your organization owner to add your email to the
                  organization members to access SiteMaster.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : !isOwner && userDataQuery.isLoading ? (
        <div className="flex items-center justify-center h-full">
          <p className="text-lg">Loading...</p>
        </div>
      ) : null}

      {!subscriptionQuery.isLoading &&
        !hasActiveSubscription &&
        organizationId ? (
        <div className="flex items-center justify-center h-full w-full bg-gradient-to-br from-gray-50 to-gray-100 p-6">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h1 className="text-3xl font-extrabold mb-2 text-gray-800 tracking-tight">
                Subscription Required
              </h1>

              <div className="h-1 w-16 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto my-4 rounded-full transform transition-all duration-300 hover:w-32"></div>

              <p className="text-gray-600 mb-8 leading-relaxed">
                Your subscription has expired or is inactive. Please renew or
                purchase a subscription to continue using all SiteMaster
                features.
              </p>

              {isOwner && (
                <button
                  onClick={() => setSubscriptionOpen(true)}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg text-white font-medium transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 group"
                >
                  <div className="flex items-center justify-center space-x-2 group-hover:space-x-4 transition-all duration-300">
                    <span>View Subscription Plans</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 transition-all duration-300 group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : organizationId ? (
        <MyProjects
          filteredSites={filteredSites}
          organizationId={organizationId}
          onNewProjectClick={onNewProjectClick}
          onBack={() => {
            window.location.reload();
          }}
        />
      ) : null}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    flex: 1,
    overflowY: "scroll",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  orgItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    width: "100px",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  orgLogo: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
  },
  orgName: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "100%",
  },
};

export default MainContent;
